import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { SectionHeader } from '@/components/ui/page-header'
import { cn } from '@/lib/utils'

export interface PageSection {
  /** Stable id for the section. */
  id: string
  /** Nav label, also used as the pane heading. */
  label: string
  /** Optional blurb on hover of the pane heading (keeps the rail quiet). */
  description?: string
  /** Optional line icon shown before the nav label. */
  icon?: LucideIcon
  /** The section's body. Only the active section is mounted. */
  content: React.ReactNode
}

/**
 * A Notion-settings-style secondary nav for breaking a long page into sections:
 * a quiet left rail of section links with one pane shown at a time. Keeps the
 * page short and calm — hairline-free, gray hover, light selected fill. On narrow
 * screens the rail becomes a horizontal strip above the pane. Only the active
 * section renders, so each section's data loads lazily when opened.
 *
 * The active section lives in the URL hash (`#spaces`), so it's deep-linkable and
 * survives refresh/share; an unknown or missing hash falls back to the first.
 */
export function SectionNav({ sections, ariaLabel = 'Sections' }: { sections: PageSection[]; ariaLabel?: string }) {
  const location = useLocation()
  const navigate = useNavigate()
  const hashId = decodeURIComponent(location.hash.replace(/^#/, ''))
  const active = sections.find((s) => s.id === hashId) ?? sections[0]
  if (!active) return null

  const select = (id: string): void => {
    // Replace (not push) so switching sections reflects in the URL without
    // stacking a history entry per click; pathname + search are preserved.
    navigate({ hash: `#${id}` }, { replace: true })
  }

  return (
    <div className="md:flex md:gap-7">
      <nav
        aria-label={ariaLabel}
        className="mb-4 flex gap-0.5 overflow-x-auto md:sticky md:top-4 md:mb-0 md:w-[200px] md:shrink-0 md:flex-col md:self-start md:overflow-visible"
      >
        {sections.map((section) => {
          const Icon = section.icon
          const selected = section.id === active.id
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => select(section.id)}
              aria-current={selected ? 'true' : undefined}
              className={cn(
                'inline-flex h-9 shrink-0 items-center gap-2.5 rounded-md px-2.5 text-left text-sm font-medium transition-colors focus-visible:focus-ds',
                selected
                  ? 'bg-accent-soft text-accent-ink'
                  : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
              )}
            >
              {Icon ? <Icon className="h-4 w-4 shrink-0" /> : null}
              <span className="whitespace-nowrap">{section.label}</span>
            </button>
          )
        })}
      </nav>

      {/* Keyed by section id so switching panes gently fades the new one in. */}
      <div key={active.id} className="min-w-0 flex-1 motion-safe:animate-[ds-fade-in_150ms_var(--ds-ease-out)]">
        <SectionHeader
          className="mb-3"
          title={active.label}
          {...(active.description ? { description: active.description } : {})}
        />
        {active.content}
      </div>
    </div>
  )
}
